import {
  Equipment,
  EquipmentRequiringInstruction,
  EquipmentWeight,
} from 'fitbeat.models';
import { IWeightAndColor } from './EquipmentWeightCalculator';

export const kettlebellsRelativeResistanceMap = new Map<number, number>([
  [2, 0.64],
  [3, 0.96],
  [4, 1.28],
  [6, 1.92],
  [8, 2.56],
  [12, 3.84],
  [16, 5.12],
  [20, 6.4],
  [24, 7.68],
  [28, 8.96],
  [32, 10.24],
]);

export const slamBallsRelativeResistanceMap = new Map<number, number>([
  [5, 1.6],
  [10, 3.2],
  [15, 4.8],
  [20, 6.4],
  [25, 8],
  [30, 9.6],
  [40, 12.8],
]);

export const sandBagsRelativeResistanceMap = new Map<number, number>([
  [5, 1.6],
  [10, 3.2],
  [15, 4.8],
  [20, 6.4],
  [25, 8],
]);

const dumbbellsRelativeResistanceMap = new Map<number, number>([
  [1, 0.32],
  [2, 0.64],
  [4, 1.28],
  [5, 1.6],
  [6, 1.92],
  [8, 2.56],
  [10, 3.2],
  [12.5, 4],
  [15, 4.8],
  [20, 6.4],
  [25, 8],
  [32.5, 10.4],
  [40, 12.8],
]);

export const relativeResistanceForStudioEquipment = new Map<
  Equipment,
  Map<number, number>
>([
  [Equipment.Dumbbells, dumbbellsRelativeResistanceMap],
  [Equipment.SandBags, sandBagsRelativeResistanceMap],
  [Equipment.SlamBalls, slamBallsRelativeResistanceMap],
  [Equipment.Kettlebells, kettlebellsRelativeResistanceMap],
]);

export const relativeResistanceForHomeEquipment = new Map<
  Equipment,
  Map<number, number>
>([[Equipment.Dumbbells, dumbbellsRelativeResistanceMap]]);

export const HomeBandsColorMap = new Map<EquipmentWeight, string>([
  ['X-LIGHT', '#ffd603'],
  ['LIGHT', '#089447'],
  ['MEDIUM', '#2b3b95'],
  ['HEAVY', '#d13327'],
  ['X-HEAVY', '#3f4040'],
  ['XX-HEAVY', '#000000'],
]);

export const StudioLongBandsColorMap = new Map<EquipmentWeight, string>([
  ['X-LIGHT', '#ffd603'],
  ['LIGHT', '#089447'],
  ['MEDIUM', '#2b3b95'],
  ['HEAVY', '#d13327'],
  ['X-HEAVY', '#3f4040'],
]);
export const StudioMiniBandsColorMap = new Map<EquipmentWeight, string>([
  ['X-LIGHT', '#ffd603'],
  ['LIGHT', '#089447'],
  ['MEDIUM', '#2b3b95'],
  ['HEAVY', '#d13327'],
]);

export const equipmentDescriptionMapping = new Map<
  EquipmentRequiringInstruction,
  string
>([
  [Equipment.Dumbbells, 'Dumbbell'],
  [Equipment.ShortUltrabands, 'Resistance Ring'],
  [Equipment.UniversalBands, 'Universal Band'],
  [Equipment.Pushbells, 'Push Bells'],
  [Equipment.Kettlebells, 'Kettlebell'],
  [Equipment.LongUltrabands, 'Long Band'],
  [Equipment.BandsHighFixed, 'Long Band'],
  [Equipment.BandsLowFixed, 'Long Band'],
  [Equipment.SlamBalls, 'Slamball'],
  [Equipment.SandBags, 'Sandbag'],
]);

export const buildBandWeightAndColor = (
  weight: EquipmentWeight,
  mapping: Map<EquipmentWeight, string>,
): IWeightAndColor => {
  return { weight, color: mapping.get(weight) };
};

export const buildHomeBandWeightAndColor = (
  weight: EquipmentWeight,
): IWeightAndColor => {
  return buildBandWeightAndColor(weight, HomeBandsColorMap);
};

export const buildStudioLongBandWeightAndColor = (weight: EquipmentWeight) => {
  return buildBandWeightAndColor(weight, StudioLongBandsColorMap);
};

export const buildStudioMiniBandWeightAndColor = (weight: EquipmentWeight) => {
  return buildBandWeightAndColor(weight, StudioMiniBandsColorMap);
};

export const homeEquipmentWeightInfoMapping = new Map<
  EquipmentRequiringInstruction,
  Map<number, IWeightAndColor>
>([
  [
    Equipment.ShortUltrabands,
    new Map([
      [1, buildHomeBandWeightAndColor('X-LIGHT')],
      [2, buildHomeBandWeightAndColor('LIGHT')],
      [3, buildHomeBandWeightAndColor('MEDIUM')],
      [4, buildHomeBandWeightAndColor('HEAVY')],
      [5, buildHomeBandWeightAndColor('X-HEAVY')],
      [6, buildHomeBandWeightAndColor('XX-HEAVY')],
    ]),
  ],
  [
    Equipment.UniversalBands,
    new Map([
      [1, buildHomeBandWeightAndColor('X-LIGHT')],
      [2, buildHomeBandWeightAndColor('LIGHT')],
      [3, buildHomeBandWeightAndColor('MEDIUM')],
      [4, buildHomeBandWeightAndColor('HEAVY')],
      [5, buildHomeBandWeightAndColor('X-HEAVY')],
      [6, buildHomeBandWeightAndColor('XX-HEAVY')],
    ]),
  ],
  [
    Equipment.Dumbbells,
    new Map([
      [0.32, { weight: '1kg' }],
      [0.64, { weight: '2kg' }],
      [1.28, { weight: '4kg' }],
      [1.6, { weight: '5kg' }],
      [1.92, { weight: '6kg' }],
      [2.56, { weight: '8kg' }],
      [3.2, { weight: '10kg' }],
      [4, { weight: '12.5kg' }],
      [4.8, { weight: '15kg' }],
      [6.4, { weight: '20kg' }],
      [8, { weight: '25kg' }],
      [10.4, { weight: '32.5kg' }],
      [12.8, { weight: '40kg' }],
    ]),
  ],
]);

export const studioEquipmentWeightInfoMapping = new Map<
  EquipmentRequiringInstruction,
  Map<number, IWeightAndColor>
>([
  [
    Equipment.ShortUltrabands,
    new Map([
      [1, buildStudioMiniBandWeightAndColor('X-LIGHT')],
      [2, buildStudioMiniBandWeightAndColor('LIGHT')],
      [3, buildStudioMiniBandWeightAndColor('MEDIUM')],
      [4, buildStudioMiniBandWeightAndColor('HEAVY')],
      [5, buildStudioMiniBandWeightAndColor('X-HEAVY')],
      [6, buildStudioMiniBandWeightAndColor('XX-HEAVY')],
    ]),
  ],
  [
    Equipment.UniversalBands,
    new Map([
      [1, buildStudioMiniBandWeightAndColor('X-LIGHT')],
      [2, buildStudioMiniBandWeightAndColor('LIGHT')],
      [3, buildStudioMiniBandWeightAndColor('MEDIUM')],
      [4, buildStudioMiniBandWeightAndColor('HEAVY')],
      [5, buildStudioMiniBandWeightAndColor('X-HEAVY')],
      [6, buildStudioMiniBandWeightAndColor('XX-HEAVY')],
    ]),
  ],
  [
    Equipment.LongUltrabands,
    new Map([
      [1, buildStudioMiniBandWeightAndColor('X-LIGHT')],
      [2, buildStudioMiniBandWeightAndColor('LIGHT')],
      [3, buildStudioMiniBandWeightAndColor('MEDIUM')],
      [4, buildStudioMiniBandWeightAndColor('HEAVY')],
      [5, buildStudioMiniBandWeightAndColor('X-HEAVY')],
    ]),
  ],
  [
    Equipment.BandsLowFixed,
    new Map([
      [1, buildStudioMiniBandWeightAndColor('X-LIGHT')],
      [2, buildStudioMiniBandWeightAndColor('LIGHT')],
      [3, buildStudioMiniBandWeightAndColor('MEDIUM')],
      [4, buildStudioMiniBandWeightAndColor('HEAVY')],
      [5, buildStudioMiniBandWeightAndColor('X-HEAVY')],
    ]),
  ],
  [
    Equipment.BandsHighFixed,
    new Map([
      [1, buildStudioMiniBandWeightAndColor('X-LIGHT')],
      [2, buildStudioMiniBandWeightAndColor('LIGHT')],
      [3, buildStudioMiniBandWeightAndColor('MEDIUM')],
      [4, buildStudioMiniBandWeightAndColor('HEAVY')],
      [5, buildStudioMiniBandWeightAndColor('X-HEAVY')],
    ]),
  ],
  [
    Equipment.Dumbbells,
    new Map([
      [0.32, { weight: '1kg' }],
      [0.64, { weight: '2kg' }],
      [1.28, { weight: '4kg' }],
      [1.6, { weight: '5kg' }],
      [1.92, { weight: '6kg' }],
      [2.56, { weight: '8kg' }],
      [3.2, { weight: '10kg' }],
      [4, { weight: '12.5kg' }],
      [4.8, { weight: '15kg' }],
      [6.4, { weight: '20kg' }],
      [8, { weight: '25kg' }],
      [10.4, { weight: '32.5kg' }],
      [12.8, { weight: '40kg' }],
    ]),
  ],
  [
    Equipment.Kettlebells,
    new Map([
      [0.64, { weight: '2kg' }],
      [0.96, { weight: '3kg' }],
      [1.28, { weight: '4kg' }],
      [1.92, { weight: '6kg' }],
      [2.56, { weight: '8kg' }],
      [3.84, { weight: '12kg' }],
      [5.12, { weight: '16kg' }],
      [6.4, { weight: '20kg' }],
      [7.68, { weight: '24kg' }],
      [8.96, { weight: '28kg' }],
      [10.24, { weight: '32kg' }],
    ]),
  ],
  [
    Equipment.SandBags,
    new Map([
      [1.6, { weight: '5kg' }],
      [3.2, { weight: '10kg' }],
      [4.8, { weight: '15kg' }],
      [6.4, { weight: '20kg' }],
      [8, { weight: '25kg' }],
    ]),
  ],
  [
    Equipment.SlamBalls,
    new Map([
      [1.6, { weight: '5kg' }],
      [3.2, { weight: '10kg' }],
      [4.8, { weight: '15kg' }],
      [6.4, { weight: '20kg' }],
      [8, { weight: '25kg' }],
      [9.6, { weight: '30kg' }],
      [12.8, { weight: '40kg' }],
    ]),
  ],
]);

export const HomeBandsUpperLimitMap = new Map<Equipment, number>([
  [Equipment.ShortUltrabands, 5],
  [Equipment.UniversalBands, 6],
]);

export const StudioBandUpperLimitMap = new Map<Equipment, number>([
  [Equipment.ShortUltrabands, 6],
  [Equipment.LongUltrabands, 5],
  [Equipment.BandsHighFixed, 5],
  [Equipment.BandsLowFixed, 5],
  [Equipment.UniversalBands, 6],
]);
